import React, { useState, useRef, useEffect } from 'react'
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline'
import type { ProductImage } from '../../types/product'

interface ImageMagnifierProps {
  backImage?: ProductImage
  detailedImage?: ProductImage
}

export const ImageMagnifier: React.FC<ImageMagnifierProps> = ({ backImage, detailedImage }) => {
  const [selectedImageType, setSelectedImageType] = useState<'back' | 'detailed'>('back')
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const MIN_ZOOM = 1
  const MAX_ZOOM = 5
  const ZOOM_STEP = 0.5

  // Get the currently selected image based on type
  const selectedImage = selectedImageType === 'back' ? backImage : detailedImage

  useEffect(() => {
    // If the currently selected image becomes unavailable, switch to the other one
    if (!selectedImage && backImage) {
      setSelectedImageType('back')
    } else if (!selectedImage && detailedImage) {
      setSelectedImageType('detailed')
    }
  }, [backImage, detailedImage, selectedImage])

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM))
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || zoom <= 1) return
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const resetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleImageChange = (type: 'back' | 'detailed') => {
    setSelectedImageType(type)
    // Keep the zoom level when switching images
    // Only reset position to center the new image
    setPosition({ x: 0, y: 0 })
  }

  if (!selectedImage) return null

  const hasBothImages = backImage && detailedImage
  const imageUrl = selectedImage.image || selectedImage.imageUrl

  return (
    <div className="space-y-4 w-full">
      {/* Thumbnail selector if both images are available */}
      {hasBothImages && (
        <div className="flex items-center gap-2">
          {/* Thumbnail for BACK image */}
          <div
            onClick={() => handleImageChange('back')}
            onKeyDown={(e) => e.key === 'Enter' && handleImageChange('back')}
            className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
              selectedImageType === 'back'
                ? 'border-sk-gold'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            style={{ width: '60px', height: '60px' }}
            role="button"
            tabIndex={0}
            aria-label="Select back image"
          >
            <img
              src={backImage.image || backImage.imageUrl}
              alt="Ingrédients (1)"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Thumbnail for DETAILED image */}
          <div
            onClick={() => handleImageChange('detailed')}
            onKeyDown={(e) => e.key === 'Enter' && handleImageChange('detailed')}
            className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
              selectedImageType === 'detailed'
                ? 'border-sk-gold'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            style={{ width: '60px', height: '60px' }}
            role="button"
            tabIndex={0}
            aria-label="Select detailed image"
          >
            <img
              src={detailedImage.image || detailedImage.imageUrl}
              alt="Ingrédients (2)"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Label showing which image is selected */}
          <div className="ml-4 text-sm text-gray-600">
            {selectedImageType === 'back' ? 'GAUCHE' : 'DROITE'}
          </div>
        </div>
      )}

      {/* Image viewer with zoom controls */}
      <div className="relative w-full">
        {/* Zoom controls */}
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            className="px-3 py-2 bg-white rounded-lg shadow-md hover:bg-gray-50 text-sm"
          >
            {Math.round(zoom * 100)}%
          </button>
        </div>

        {/* Image container */}
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-lg border border-gray-300 bg-gray-50 w-full"
          style={{ 
            height: '400px',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onKeyDown={(e) => {
            if (e.key === 'Escape') resetZoom()
          }}
          role="button"
          tabIndex={0}
          aria-label="Zoomable product image - use mouse to drag when zoomed, press Escape to reset zoom"
        >
          <div className="w-full h-full relative flex items-center justify-center overflow-hidden">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Product ingredients"
              className="select-none max-w-full max-h-full object-contain"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transformOrigin: 'center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}